import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Globe, MapPin, Map as MapIcon, X, ChevronDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/geo/search`;

const TYPE_META = {
  country: { icon: Globe, label: "France entière", color: "text-brand-moss" },
  region: { icon: Globe, label: "Région", color: "text-brand-moss" },
  department: { icon: MapIcon, label: "Département", color: "text-brand-ink" },
  city: { icon: MapPin, label: "Ville", color: "text-brand-ink" },
};

/**
 * Combobox unifié pour sélectionner régions / départements / villes.
 * `value` est un tableau d'items `{type, code, label, sublabel, departments, postal_codes}`.
 */
export default function GeoPicker({ value = [], onChange, label = "Zone de recherche" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchResults = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await axios.get(API, { params: { q, limit: 20 } });
      setResults(r.data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 220);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, fetchResults]);

  const toggle = (item) => {
    const exists = value.some((v) => v.code === item.code);
    // Sélectionner "France entière" vide la sélection (= tout le pays)
    if (item.type === "country") {
      onChange(exists ? [] : []);
      return;
    }
    onChange(exists ? value.filter((v) => v.code !== item.code) : [...value, item]);
  };

  const remove = (code) => onChange(value.filter((v) => v.code !== code));

  const groups = results.reduce((acc, r) => {
    const meta = TYPE_META[r.type] || TYPE_META.city;
    (acc[meta.label] = acc[meta.label] || []).push(r);
    return acc;
  }, {});
  const groupOrder = ["France entière", "Région", "Département", "Ville"];

  const summary =
    value.length === 0
      ? "France entière (toutes les villes)"
      : value.length === 1
        ? value[0].label
        : `${value.length} zones sélectionnées`;

  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="geopicker-trigger"
            className="mt-1 w-full h-10 flex items-center justify-between gap-2 rounded-md border border-brand-line bg-white px-3 text-sm text-brand-ink hover:border-brand-ink/25 transition-colors"
          >
            <span className="flex items-center gap-2 min-w-0">
              {value.length === 0 ? (
                <Globe className="h-4 w-4 text-brand-moss flex-shrink-0" strokeWidth={1.5} />
              ) : (
                <MapPin className="h-4 w-4 text-brand-moss flex-shrink-0" strokeWidth={1.5} />
              )}
              <span className="truncate">{summary}</span>
            </span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60 flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-[380px] sm:w-[460px]" data-testid="geopicker-popover">
          <Command shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Rechercher une ville, département ou région..."
              data-testid="geopicker-search"
            />
            <CommandList className="max-h-[340px]">
              {loading && (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-brand-subtle">
                  <Loader2 className="h-3 w-3 animate-spin" /> Recherche…
                </div>
              )}
              {!loading && results.length === 0 && <CommandEmpty>Aucune zone trouvée.</CommandEmpty>}
              {groupOrder
                .filter((g) => groups[g])
                .map((groupName) => (
                  <CommandGroup key={groupName} heading={groupName}>
                    {groups[groupName].map((item) => {
                      const meta = TYPE_META[item.type] || TYPE_META.city;
                      const Icon = meta.icon;
                      const selected = value.some((v) => v.code === item.code);
                      return (
                        <CommandItem
                          key={item.code}
                          value={item.code}
                          onSelect={() => toggle(item)}
                          data-testid={`geopicker-item-${item.code}`}
                          className="cursor-pointer gap-2"
                        >
                          <Icon className={`h-4 w-4 ${meta.color}`} strokeWidth={1.5} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-brand-ink truncate">{item.label}</div>
                            {item.sublabel && (
                              <div className="text-[11px] text-brand-subtle truncate">{item.sublabel}</div>
                            )}
                          </div>
                          <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? "bg-brand-moss border-brand-moss" : "border-brand-line"}`}>
                            {selected && <span className="h-1.5 w-1.5 bg-white rounded-full" />}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="geopicker-pills">
          {value.map((item) => {
            const Icon = (TYPE_META[item.type] || TYPE_META.city).icon;
            return (
              <Badge
                key={item.code}
                variant="secondary"
                className="bg-brand-paper text-brand-ink/90 border border-brand-line pl-2 pr-1 py-0.5 gap-1.5"
                data-testid={`geopicker-pill-${item.code}`}
              >
                <Icon className="h-3 w-3 text-brand-moss" strokeWidth={1.75} />
                <span className="text-[11px]">{item.label}</span>
                <button
                  type="button"
                  onClick={() => remove(item.code)}
                  className="hover:bg-brand-line rounded-full p-0.5"
                  aria-label={`Retirer ${item.label}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
